import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import StepIndicator from './components/StepIndicator';
import ExperienceLevelStep from './components/ExperienceLevelStep';
import EquipmentStep from './components/EquipmentStep';
import LocationStep from './components/LocationStep';
import InterestsStep from './components/InterestsStep';
import NotificationStep from './components/NotificationStep';
import CompletionStep from './components/CompletionStep';

const OnboardingFlow = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  const [onboardingData, setOnboardingData] = useState({
    experienceLevel: '',
    hasOwnBoard: false,
    equipment: [],
    locations: [],
    interests: [],
    notifications: {
      events: true,
      messages: true,
      marketplace: false,
      community: false,
      weather: true,
      newsletter: false
    }
  });

  const steps = [
    { id: 1, title: 'Deneyim', component: ExperienceLevelStep },
    { id: 2, title: 'Ekipman', component: EquipmentStep },
    { id: 3, title: 'Bölgeler', component: LocationStep },
    { id: 4, title: 'İlgi Alanları', component: InterestsStep },
    { id: 5, title: 'Bildirimler', component: NotificationStep },
    { id: 6, title: 'Tamamla', component: CompletionStep }
  ];

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      navigate('/login', { replace: true });
    }
  }, [navigate]);

  const handleExperienceLevelSelect = (level) => {
    setOnboardingData(prev => ({ ...prev, experienceLevel: level }));
  };

  const handleEquipmentToggle = (equipmentId) => {
    setOnboardingData(prev => ({
      ...prev,
      equipment: prev?.equipment?.includes(equipmentId)
        ? prev?.equipment?.filter(id => id !== equipmentId)
        : [...prev?.equipment, equipmentId]
    }));
  };

  const handleBoardOwnershipChange = (hasBoard) => {
    setOnboardingData(prev => ({
      ...prev,
      hasOwnBoard: hasBoard,
      equipment: hasBoard ? prev?.equipment : []
    }));
  };

  const handleLocationToggle = (locationId) => {
    setOnboardingData(prev => ({
      ...prev,
      locations: prev?.locations?.includes(locationId)
        ? prev?.locations?.filter(id => id !== locationId)
        : [...prev?.locations, locationId]
    }));
  };

  const handleInterestToggle = (interestId) => {
    setOnboardingData(prev => ({
      ...prev,
      interests: prev?.interests?.includes(interestId)
        ? prev?.interests?.filter(id => id !== interestId)
        : [...prev?.interests, interestId]
    }));
  };

  const handleNotificationToggle = (notificationId, value) => {
    setOnboardingData(prev => ({
      ...prev,
      notifications: {
        ...prev?.notifications,
        [notificationId]: value
      }
    }));
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return onboardingData?.experienceLevel !== '';
      case 2:
        return true;
      case 3:
        return onboardingData?.locations?.length > 0;
      case 4:
        return onboardingData?.interests?.length > 0;
      case 5:
        return true;
      case 6:
        return true;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (currentStep < steps?.length) {
      setCurrentStep(prev => prev + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSkip = () => {
    if (currentStep < steps?.length - 1) {
      setCurrentStep(prev => prev + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleComplete = async () => {
    setIsLoading(true);
    
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      localStorage.setItem('onboardingCompleted', 'true');
      localStorage.setItem('userPreferences', JSON.stringify(onboardingData));
      
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      user.onboardingCompleted = true;
      user.experienceLevel = onboardingData?.experienceLevel;
      localStorage.setItem('user', JSON.stringify(user));
      
      navigate('/event-discovery', { replace: true });
    } catch (error) {
      console.error('Onboarding completion error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const CurrentStepComponent = steps?.[currentStep - 1]?.component;

  const getStepProps = () => {
    switch (currentStep) {
      case 1:
        return {
          selectedLevel: onboardingData?.experienceLevel,
          onSelect: handleExperienceLevelSelect
        };
      case 2:
        return {
          selectedEquipment: onboardingData?.equipment,
          onToggle: handleEquipmentToggle,
          hasOwnBoard: onboardingData?.hasOwnBoard,
          onBoardOwnershipChange: handleBoardOwnershipChange
        };
      case 3:
        return {
          selectedLocations: onboardingData?.locations,
          onToggle: handleLocationToggle
        };
      case 4:
        return {
          selectedInterests: onboardingData?.interests,
          onToggle: handleInterestToggle
        };
      case 5:
        return {
          notifications: onboardingData?.notifications,
          onToggle: handleNotificationToggle
        };
      case 6:
        return {
          userData: {
            name: JSON.parse(localStorage.getItem('user') || '{}')?.name || 'Paddler',
            ...onboardingData
          },
          onComplete: handleComplete
        };
      default:
        return {};
    }
  };

  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      <div className="fixed top-0 left-0 right-0 bg-[var(--color-card)] border-b border-[var(--color-border)] z-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-4 md:py-5 flex items-center justify-between">
          <div className="flex items-center gap-3 md:gap-4">
            <div className="w-10 h-10 md:w-12 md:h-12 bg-[var(--color-primary)] rounded-lg md:rounded-xl flex items-center justify-center">
              <Icon name="Waves" size={24} className="text-[var(--color-primary-foreground)]" />
            </div>
            <div>
              <h1 className="text-lg md:text-xl lg:text-2xl font-bold text-[var(--color-foreground)]">
                PaddleTurkiye
              </h1>
              <p className="text-xs md:text-sm text-[var(--color-muted-foreground)]">
                Profil Oluşturma
              </p>
            </div>
          </div>
          
          {currentStep < steps?.length && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSkip}
              iconName="SkipForward"
              iconPosition="right"
            >
              Atla
            </Button>
          )}
        </div>
      </div>
      <div className="pt-20 md:pt-24 lg:pt-28">
        <StepIndicator
          currentStep={currentStep}
          totalSteps={steps?.length}
          steps={steps}
        />

        <div className="pb-32 md:pb-36 lg:pb-40">
          <CurrentStepComponent {...getStepProps()} />
        </div>
      </div>
      {currentStep < steps?.length && (
        <div className="fixed bottom-0 left-0 right-0 bg-[var(--color-card)] border-t border-[var(--color-border)] z-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-4 md:py-5 lg:py-6">
            <div className="flex items-center justify-between gap-4">
              <Button
                variant="outline"
                size="lg"
                onClick={handlePrevious}
                disabled={currentStep === 1}
                iconName="ArrowLeft"
                iconPosition="left"
              >
                Geri
              </Button>

              <div className="flex-1 text-center">
                <p className="text-sm md:text-base text-[var(--color-muted-foreground)]">
                  {currentStep} / {steps?.length}
                </p>
              </div>

              <Button
                variant="default"
                size="lg"
                onClick={handleNext}
                disabled={!canProceed()}
                iconName="ArrowRight"
                iconPosition="right"
              >
                {currentStep === steps?.length - 1 ? 'Tamamla' : 'İleri'}
              </Button>
            </div>
          </div>
        </div>
      )}
      {isLoading && (
        <div className="fixed inset-0 bg-[rgba(0,0,0,0.5)] flex items-center justify-center z-[100]">
          <div className="bg-[var(--color-card)] rounded-2xl p-8 md:p-10 text-center max-w-md mx-4">
            <div className="w-16 h-16 md:w-20 md:h-20 border-4 border-[var(--color-muted)] border-t-[var(--color-primary)] rounded-full animate-spin mx-auto mb-6" />
            <h3 className="text-xl md:text-2xl font-bold text-[var(--color-foreground)] mb-2">
              Profiliniz Hazırlanıyor
            </h3>
            <p className="text-sm md:text-base text-[var(--color-muted-foreground)]">
              Size özel içerikler oluşturuluyor...
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default OnboardingFlow;